import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Phone,
  Users,
  UserPlus,
  Clock,
  CheckSquare,
  CalendarDays,
  Play,
  Square,
  ArrowRight,
  TrendingUp,
  Globe,
} from "lucide-react";
import { getCurrentSydneyDisplay, formatTimeSydney } from "../../utils/timezone";

export default function StaffDashboard() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [currentTime, setCurrentTime] = useState(getCurrentSydneyDisplay());

  const fetchStats = async () => {
    try {
      const response = await api().get("/dashboard/staff");
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // Update time every second
    const interval = setInterval(() => {
      setCurrentTime(getCurrentSydneyDisplay());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleClockIn = async () => {
    setClockingIn(true);
    try {
      await api().post("/timesheet/clock-in", {});
      toast.success("Clocked in successfully!");
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clock in");
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    setClockingOut(true);
    try {
      await api().post("/timesheet/clock-out", { break_minutes: 0 });
      toast.success("Clocked out successfully!");
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clock out");
    } finally {
      setClockingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const metricCards = [
    {
      label: "Calls Today",
      value: stats?.today_calls || 0,
      subValue: `${stats?.week_calls || 0} this week`,
      icon: Phone,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      label: "Potential Leads",
      value: stats?.today_potential || 0,
      subValue: `${stats?.week_potential || 0} this week`,
      icon: Users,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
    },
    {
      label: "Signups",
      value: stats?.today_signups || 0,
      subValue: `${stats?.week_signups || 0} this week`,
      icon: UserPlus,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
    },
    {
      label: "Hours Worked",
      value: `${stats?.week_hours || 0}h`,
      subValue: `${stats?.month_hours || 0}h this month`,
      icon: Clock,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
  ];

  return (
    <div className="space-y-8" data-testid="staff-dashboard">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Welcome back! Here's your overview.</p>
          <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
            <Globe className="h-3 w-3" />
            Sydney Time: {currentTime}
          </p>
        </div>
        
        {/* Clock In/Out */}
        <div className="flex gap-3">
          {stats?.is_clocked_in ? (
            <Button
              onClick={handleClockOut}
              disabled={clockingOut}
              className="bg-red-500 hover:bg-red-600 btn-active"
              data-testid="clock-out-btn"
            >
              <Square className="mr-2 h-4 w-4" />
              {clockingOut ? "Clocking out..." : "Clock Out"}
            </Button>
          ) : (
            <Button
              onClick={handleClockIn}
              disabled={clockingIn}
              className="bg-emerald-500 hover:bg-emerald-600 btn-active"
              data-testid="clock-in-btn"
            >
              <Play className="mr-2 h-4 w-4" />
              {clockingIn ? "Clocking in..." : "Clock In"}
            </Button>
          )}
        </div>
      </div>

      {/* Status Badge */}
      {stats?.is_clocked_in && (
        <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg animate-fade-in">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse-soft" />
          <span className="text-sm font-medium text-emerald-700">
            Currently clocked in {stats?.clock_in_time ? `since ${formatTimeSydney(stats.clock_in_time)}` : ""}
          </span>
        </div>
      )}

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricCards.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <Card
              key={metric.label}
              className={`metric-card card-hover animate-fade-in stagger-${index + 1}`}
              data-testid={`metric-${metric.label.toLowerCase().replace(" ", "-")}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="metric-label">{metric.label}</p>
                    <p className="metric-value mt-2">{metric.value}</p>
                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      {metric.subValue}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${metric.bgColor}`}>
                    <Icon className={`h-5 w-5 ${metric.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions & Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/portal/activities" data-testid="quick-log-activity">
              <Button variant="outline" className="w-full justify-between group">
                <span className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-blue-600" />
                  Log Daily Activity
                </span>
                <ArrowRight className="h-4 w-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/portal/todos" data-testid="quick-add-task">
              <Button variant="outline" className="w-full justify-between group">
                <span className="flex items-center gap-2">
                  <CheckSquare className="h-4 w-4 text-emerald-600" />
                  Add New Task
                </span>
                <ArrowRight className="h-4 w-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/portal/leave" data-testid="quick-apply-leave">
              <Button variant="outline" className="w-full justify-between group">
                <span className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-purple-600" />
                  Apply for Leave
                </span>
                <ArrowRight className="h-4 w-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Pending Items */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Pending Items</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <CheckSquare className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Pending Tasks</p>
                  <p className="text-sm text-slate-500">Items requiring attention</p>
                </div>
              </div>
              <Badge variant="secondary" className="text-lg font-mono" data-testid="pending-todos-count">
                {stats?.pending_todos || 0}
              </Badge>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CalendarDays className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">Leave Requests</p>
                  <p className="text-sm text-slate-500">Awaiting approval</p>
                </div>
              </div>
              <Badge variant="secondary" className="text-lg font-mono" data-testid="pending-leaves-count">
                {stats?.pending_leaves || 0}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
